import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { logger } from '@/utils/logger';
import { en } from '@/i18n/translations/en';
import { fr } from '@/i18n/translations/fr';
import { de } from '@/i18n/translations/de';
import { it } from '@/i18n/translations/it';
import { nl } from '@/i18n/translations/nl';
import { pt } from '@/i18n/translations/pt';
import { ja } from '@/i18n/translations/ja';
import { ko } from '@/i18n/translations/ko';
import { ru } from '@/i18n/translations/ru';
import { ar } from '@/i18n/translations/ar';
import { hi } from '@/i18n/translations/hi';

export type Language = 'en' | 'zh' | 'es' | 'fr' | 'de' | 'it' | 'nl' | 'ja' | 'ko' | 'pt' | 'ru' | 'ar' | 'hi';

export interface LanguageInfo {
  code: Language;
  name: string;
  nativeName: string;
  flag: string;
}

/* 13 languages — 11 previously supported + Italian and Dutch which
   were declared in src/i18n/locales.ts and shipped in sitemap-it-it.xml
   / sitemap-nl-nl.xml but had no LanguageContext entry, so any t() call
   in an it-it or nl-nl route silently fell back to English. */
export const supportedLanguages: LanguageInfo[] = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', flag: '🇳🇱' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇵🇹' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: 'Korean', nativeName: '한국어', flag: '🇰🇷' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳' },
];

interface LanguageContextType {
  language: Language;
  languageInfo: LanguageInfo;
  setLanguage: (language: Language) => void;
  supportedLanguages: LanguageInfo[];
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

/* Zh and Es keep their partial translations inline until we move them
   to per-locale files too. Every other locale is now sourced from
   src/i18n/translations/<locale>.ts, generated by
   scripts/build-locale-translations.mjs so re-running the script picks
   up new English keys without touching this file. */
const legacyPartialZh: Record<string, string> = {
  'nav.home': '首页', 'nav.about': '关于我们', 'nav.products': '产品',
  'nav.capabilities': '能力', 'nav.quality': '质量与合规',
  'nav.news': '新闻', 'nav.contact': '联系我们',
  'header.phone': '电话', 'header.email': '邮箱', 'header.rfq_cart': '购物车',
  'button.request_quote': '请求报价',
  'search.placeholder': '按 CAS 编号或产品名称搜索',
  'language.change': '更改语言',
  'hero.badge': 'ISO 9001:2015 认证',
  'hero.title.leading': '领先的', 'hero.title.pharmaceutical': '医药',
  'hero.title.innovation': '创新',
  'hero.button.explore': '探索产品', 'hero.button.quote': '请求报价',
  'cta.title': '准备与我们合作?',
  'cta.quote_now': '立即报价', 'cta.download_catalog': '下载目录',
  'product.cas': 'CAS', 'product.moq': '起订量',
  'product.details': '详情', 'product.sds': 'SDS',
  'product.add_rfq': '加入购物车',
  'footer.copyright': '© 2026 Longshine Boi-Tech Pvt. Ltd. 版权所有。',
  'common.search': '搜索', 'common.loading': '加载中...',
  'common.error': '错误', 'common.success': '成功',
};
const legacyPartialEs: Record<string, string> = {
  'nav.home': 'Inicio', 'nav.about': 'Sobre nosotros', 'nav.products': 'Productos',
  'nav.capabilities': 'Capacidades', 'nav.quality': 'Calidad y Cumplimiento',
  'nav.news': 'Noticias', 'nav.contact': 'Contacto',
  'header.phone': 'Teléfono', 'header.email': 'Correo', 'header.rfq_cart': 'Carrito',
  'button.request_quote': 'Solicitar cotización',
  'search.placeholder': 'Buscar por nº CAS o nombre',
  'language.change': 'Cambiar idioma',
  'hero.badge': 'Excelencia certificada ISO 9001:2015',
  'hero.title.leading': 'Líder en', 'hero.title.pharmaceutical': 'innovación',
  'hero.title.innovation': 'farmacéutica',
  'hero.button.explore': 'Explorar productos', 'hero.button.quote': 'Solicitar cotización',
  'cta.title': '¿Listo para asociarse?',
  'cta.quote_now': 'Solicitar cotización ahora', 'cta.download_catalog': 'Descargar catálogo',
  'product.cas': 'CAS', 'product.moq': 'MOQ',
  'product.details': 'Detalles', 'product.sds': 'HDS',
  'product.add_rfq': 'Añadir al carrito',
  'footer.copyright': '© 2026 Longshine Boi-Tech Pvt. Ltd. Todos los derechos reservados.',
  'common.search': 'Buscar', 'common.loading': 'Cargando...',
  'common.error': 'Error', 'common.success': 'Éxito',
};

const translations: Record<Language, Record<string, string>> = {
  en, fr, de, it, nl, pt, ja, ko, ru, ar, hi,
  zh: legacyPartialZh,
  es: legacyPartialEs,
};

// Detect user's browser language
const detectBrowserLanguage = (): Language => {
  if (typeof window === 'undefined') return 'en';
  const browserLang = navigator.language.toLowerCase();
  const languageCode = browserLang.split('-')[0] as Language;
  if (supportedLanguages.some(lang => lang.code === languageCode)) return languageCode;
  return 'en';
};

// Get stored language or detect browser language
const getInitialLanguage = (): Language => {
  if (typeof window === 'undefined') return 'en';
  try {
    const stored = localStorage.getItem('preferred-language') as Language;
    if (stored && supportedLanguages.some(lang => lang.code === stored)) return stored;
    return detectBrowserLanguage();
  } catch (error) {
    logger.warn('Error accessing localStorage:', error);
    return 'en';
  }
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  const setLanguage = (newLanguage: Language) => {
    setLanguageState(newLanguage);
    try {
      localStorage.setItem('preferred-language', newLanguage);
    } catch (error) {
      logger.warn('Error saving language to localStorage:', error);
    }
    if (typeof document !== 'undefined') {
      document.documentElement.lang = newLanguage;
      document.documentElement.dir = newLanguage === 'ar' ? 'rtl' : 'ltr';
    }
  };

  /* Cascade: active locale → English source → raw key. Falling back to
     English (instead of returning the dot-key literal) means a
     partially-translated locale still renders a fully-formed page —
     users see English on secondary surfaces rather than gibberish
     like `about.milestone.2015`. */
  const t = (key: string): string => translations[language]?.[key] || translations.en[key] || key;

  const languageInfo = supportedLanguages.find(lang => lang.code === language) || supportedLanguages[0];

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language;
      document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    }
  }, [language]);

  const value: LanguageContextType = {
    language, languageInfo, setLanguage, supportedLanguages, t,
  };

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    if (import.meta.env.DEV) {
      logger.warn('useLanguage used outside LanguageProvider, using fallback');
      return {
        language: 'en',
        languageInfo: supportedLanguages[0],
        setLanguage: () => {},
        supportedLanguages,
        t: (key: string) => translations.en[key] || key,
      };
    }
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
